<script>
  import { createEventDispatcher, setContext } from 'svelte'
  import { writable, derived } from 'svelte/store'
  import { faEllipsisH } from '@fortawesome/pro-regular-svg-icons'
  import filterClasses, { cssPrefix } from '../helper-classes.js'
  import { getId } from '../FormElements/builder.js'
  import { Loader } from '../Loader'
  import Icon from '../Icon/'

  export let id = null
  export let data = []
  export let selectable = true
  export let header = true
  export let fullWidth = false
  export let stickyHead = false
  export let scrollable = false
  export let variant = null
  export let showEmpty = true
  export let loading = false
  export let hasExpansions = false
  export let mobile = true
  export let align = null
  export let valign = null
  export let key = null

  export let style = null
  export let helperClass = null
  let importedClasses = ''
  export { importedClasses as class }
  export let classPermissiveMode = false
  if (helperClass) {
    console.debug("[DS:deprecation] please replace 'helperClass' prop by 'class'")
    if (!importedClasses) {
      importedClasses = helperClass
    }
  }
  const classes = [`${cssPrefix}table`]
  if (importedClasses) classes.push(...filterClasses(importedClasses, classPermissiveMode))
  if (align) classes.push(`is-align-${align}`)
  if (valign) classes.push(`is-valign-${valign}`)

  if (variant) classes.push('is-variant-' + variant)

  // mutex to get only one actionable like dropdown active
  const bound = writable(false)
  setContext('dataTableBound', bound)

  // track cells
  const cellsCounter = writable(0)
  setContext('dataTableCellsCounter', {
    increment: () => cellsCounter.update(value => value + 1),
    decrement: () => cellsCounter.update(value => value - 1)
  })
  const selectableStore = writable(selectable)
  $: selectableStore.set(selectable)
  const colspan = derived([cellsCounter, selectableStore], ([$cells, $selectable]) => $cells + ($selectable ? 1 : 0))
  setContext('dataTableColspanStore', colspan)

  const dispatch = createEventDispatcher()

  if (fullWidth) classes.push('is-fullwidth')

  if (stickyHead) classes.push('has-sticky-head')

  if (scrollable) classes.push('is-scrollable')

  if (mobile) classes.push('is-mobile')

  let allSelected = false
  let indeterminate = false

  const checkAll = () => {
    if (data.every(row => row.selectable === false || row.selected)) {
      indeterminate = false
      allSelected = false
    }
    data.forEach(row => {
      if (row.selectable !== false) row.selected = allSelected
    })
    data = data
    dispatch('change', { selected: getSelected() })
  }

  const checkOne = r => {
    let selected = r.selected
    r.selected = !selected
    data = data

    indeterminate = data.some(
      row => row.selectable !== false && (typeof row.selected === 'boolean' ? row.selected : false) !== selected
    )
    allSelected = selected && !indeterminate
    dispatch('change', { selected: getSelected() })
  }

  const updateHeaderSelectionCheckboxState = data => {
    if (!Array.isArray(data)) return
    allSelected = data.every(row => row.selectable === false || row.selected) && data.length > 0
    indeterminate = !allSelected && data.some(row => row.selectable !== false && row.selected)
  }

  export const getSelected = () => {
    data.forEach(row => {
      delete row.__id
    })

    return data.filter(row => row.selectable !== false && row.selected)
  }

  $: updateHeaderSelectionCheckboxState(data)
  $: showLoading = loading

  data = data.map(o => ({ __id: getId(), ...o }))
</script>

<div class={classes.join(' ')} {style} {id}>
  <table>
    {#if header}
      <thead>
        <tr>
          {#if selectable}
            <th style="width:50px">
              <div class={`${cssPrefix}table-column`}>
                <div class={`${cssPrefix}checkbox`}>
                  <label>
                    <input bind:checked={allSelected} bind:indeterminate type="checkbox" on:change={checkAll} />
                    <span class={`${cssPrefix}toggle-shape`} />
                  </label>
                </div>
              </div>
            </th>
          {/if}
          <slot row={{}} index={0} />
          {#if hasExpansions}
            <th />
          {/if}
        </tr>
      </thead>
    {/if}

    <tbody>
      {#if showLoading}
        <tr class={`${cssPrefix}table-busy`}>
          <td colspan={$colspan}>
            <slot name="loader">
              <Loader class="mx-auto size-medium" />
            </slot>
          </td>
        </tr>
      {:else if data.length === 0 && showEmpty}
        <tr class={`${cssPrefix}table-busy`}>
          <td colspan={$colspan}>
            <slot name="empty">
              <div class={`${cssPrefix}message`} style="text-align: center">There is no data available to be shown</div>
            </slot>
          </td>
        </tr>
      {:else}
        {#each data as row, i}
          <tr
            id={key ? row[key] : row.__id}
            class:gl-has-expansion={row.isExpanded}
            class:gl-selected={row.selected}
            class:gl-dark-row={row.dark}
            on:click|preventDefault|stopPropagation={() => {
              checkOne(row)
            }}
          >
            {#if selectable}
              <td>
                {#if row.selectable !== false}
                  <div class={`${cssPrefix}checkbox`}>
                    <label>
                      <input checked={row.selected} type="checkbox" />
                      <span class={`${cssPrefix}toggle-shape`} />
                    </label>
                  </div>
                {/if}
              </td>
            {/if}
            <slot {row} index={i + 1} />
            {#if hasExpansions}
              <td class="is-table-expand">
                <button
                  on:click|preventDefault|stopPropagation={() => {
                    row.isExpanded = !row.isExpanded
                    dispatch('toggleExpansion', row)
                  }}
                  class={`${cssPrefix}table-expand`}
                  class:is-selected={row.isExpanded}
                  id={key ? `${row[key]}-expand` : `${row.__id}-expand`}
                  title="expansion-button"
                >
                  <Icon icon={faEllipsisH} />
                </button>
              </td>
            {/if}
          </tr>
          <slot name="expansion" {row} index={i + 1} />
        {/each}
      {/if}
    </tbody>
  </table>
</div>
